'use client';

import IconButton from '@/components/base/button/icon-button';
import useAnimatedMount from '@/hooks/use-animated-mount';
import { useBreakpointUp } from '@/hooks/use-theme';
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useFloating,
  useId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { XCircle } from '@phosphor-icons/react/dist/ssr';
import { useClickOutside } from '@react-hookz/web';
import { clsx } from 'clsx';
import { useTranslations } from 'next-intl';
import type { ReactNode, RefObject } from 'react';
import { mergeRefs } from 'react-merge-refs';

interface Props {
  children?: ReactNode;
  footer?: ReactNode;
  title: string;
  isOpen?: boolean;
  setIsOpen?: (newOpenState: boolean) => void;
  fullScreen?: boolean;
  className?: string;
  onCloseAction?: () => void;
  testId?: string;
  titleClassName?: string;
  closeOnBackdropClick?: boolean;
  wrapperClassName?: string;
}

const animationOptions = {
  beforeEntry: clsx('anim-pop-in scale-90 opacity-0'),
  entry: 'anim-pop-in',
  beforeExit: 'anim-pop-out',
  exit: clsx('anim-pop-out scale-90 opacity-0'),
} as const;

const backdropAnimationOptions = {
  beforeEntry: clsx('anim-pop-in opacity-0'),
  entry: 'anim-pop-in',
  beforeExit: 'anim-pop-out',
  exit: clsx('anim-pop-out opacity-0'),
} as const;

const Modal = ({
  title,
  isOpen,
  setIsOpen,
  children,
  footer,
  fullScreen,
  className,
  onCloseAction,
  testId,
  titleClassName,
  closeOnBackdropClick = true,
  wrapperClassName,
}: Props) => {
  const t = useTranslations('common');

  const { refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
  });

  const role = useRole(context);

  const { getFloatingProps } = useInteractions([role]);

  const labelId = useId();

  const {
    mounted,
    className: animatedClassName,
    ref,
  } = useAnimatedMount(isOpen, animationOptions);
  const { className: backdropClassName, ref: backdropRef } =
    useAnimatedMount<HTMLDivElement>(isOpen, backdropAnimationOptions);

  const isDesktop = useBreakpointUp('s');

  const handleClose = () => {
    onCloseAction?.();
    setIsOpen?.(false);
  };

  //TODO: Remove the RefObject<HTMLDivElement> when useClickOutside will be ready for react 19(not ref can't be null)

  const handleClickOutside = () => {
    if (!closeOnBackdropClick) return;
    handleClose();
  };
  useClickOutside<HTMLElement>(
    ref as RefObject<HTMLElement>,
    handleClickOutside,
  );

  return (
    mounted && (
      <FloatingPortal>
        <FloatingOverlay
          lockScroll
          ref={backdropRef}
          className={clsx(
            'z-top flex flex-col bg-overlay backdrop-blur',
            fullScreen ? 'items-stretch' : 'items-center justify-center',
            backdropClassName,
          )}
        >
          <FloatingFocusManager context={context}>
            <div
              className={clsx(
                'l:m-l m:m-m s:m-s xxl:m-xxl flex flex-col overflow-hidden bg-main xl:m-xl',
                fullScreen ? 'm-0 grow s:rounded-m' : 'm-xxs xs:m-xs rounded-m',
                !fullScreen && 'max-w-max-modal-width',
                className,
                animatedClassName,
              )}
              aria-labelledby={labelId}
              role="dialog"
              data-testid={testId}
              ref={mergeRefs([refs.setFloating, ref])}
              {...getFloatingProps()}
            >
              <div
                className={clsx(
                  'flex w-full items-center border-decorative border-b-1 pr-xxs pl-inner',
                  fullScreen && 'border-t-brand max-s:border-t-6',
                  titleClassName,
                )}
              >
                {fullScreen && <div className="s:hidden flex-1"></div>}
                <span
                  className={clsx(
                    'py-inner text-heading-m',
                    fullScreen && 'max-s:text-center',
                  )}
                  id={labelId}
                >
                  {title}
                </span>
                {setIsOpen && (
                  <>
                    <div className="flex flex-1 justify-end">
                      <IconButton
                        Icon={XCircle}
                        size={isDesktop ? 'large' : 'default'}
                        className="!p-0"
                        iconVariant="regular"
                        aria-label={t('close')}
                        onClick={handleClose}
                        testId="close-modal"
                      />
                    </div>
                  </>
                )}
              </div>
              <div className={clsx('flex-1 overflow-y-auto', wrapperClassName)}>
                {children}
              </div>
              {footer && (
                <div className="border-t-1 border-t-decorative p-inner">
                  {footer}
                </div>
              )}
            </div>
          </FloatingFocusManager>
        </FloatingOverlay>
      </FloatingPortal>
    )
  );
};

Modal.displayName = 'Modal';

export default Modal;
